import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar
} from '@mui/material';
import {
  Search,
  Download,
  Login,
  Logout,
  PersonAdd,
  Edit,
  Lock,
  Payment,
  Event,
  Article,
  AttachMoney,
  Cancel,
  CheckCircle,
  Error
} from '@mui/icons-material';
import { format } from 'date-fns';
import axios from '../utils/axios';

const UserActivityHistory = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [activityType, setActivityType] = useState('');
  const [dateRange, setDateRange] = useState('7');
  const [view, setView] = useState('table'); // 'table' or 'timeline'
  const [activitySummary, setActivitySummary] = useState(null);

  const activityIcons = {
    LOGIN: <Login />,
    LOGOUT: <Logout />,
    REGISTER: <PersonAdd />,
    PROFILE_UPDATE: <Edit />,
    PASSWORD_CHANGE: <Lock />,
    SUBSCRIPTION_PURCHASE: <AttachMoney />,
    SUBSCRIPTION_CANCEL: <Cancel />,
    EVENT_REGISTER: <Event />,
    EVENT_ATTEND: <CheckCircle />,
    BLOG_VIEW: <Article />,
    BLOG_COMMENT: <Article />,
    PAYMENT_SUCCESS: <Payment />,
    PAYMENT_FAILED: <Error />
  };

  const activityColors = {
    LOGIN: 'primary',
    LOGOUT: 'secondary',
    REGISTER: 'success',
    PROFILE_UPDATE: 'info',
    PASSWORD_CHANGE: 'warning',
    SUBSCRIPTION_PURCHASE: 'success',
    SUBSCRIPTION_CANCEL: 'error',
    EVENT_REGISTER: 'primary',
    EVENT_ATTEND: 'success',
    BLOG_VIEW: 'info',
    BLOG_COMMENT: 'primary',
    PAYMENT_SUCCESS: 'success',
    PAYMENT_FAILED: 'error'
  };

  useEffect(() => {
    fetchActivities();
    fetchActivitySummary();
  }, [page, rowsPerPage, activityType, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        type: activityType || undefined,
        days: dateRange
      };

      const response = await axios.get('/api/users/activity', { params });
      setActivities(response.data.activities);
      setTotalItems(response.data.pagination.totalItems);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch activities');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivitySummary = async () => {
    try {
      const response = await axios.get('/api/activities/summary', {
        params: { days: dateRange }
      });
      setActivitySummary(response.data);
    } catch (err) {
      console.error('Failed to fetch activity summary:', err);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExport = async () => {
    try {
      const response = await axios.get('/api/users/activity/export', {
        params: {
          type: activityType || undefined,
          days: dateRange
        },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `activity-history-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to export activities');
    }
  };

  const formatActivityDate = (date) => {
    return format(new Date(date), 'MMM dd, yyyy HH:mm:ss');
  };

  const getActivityIcon = (type) => {
    return activityIcons[type] || <Event />;
  };

  const getActivityColor = (type) => {
    return activityColors[type] || 'default';
  };

  if (loading && !activities.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Activity History
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search activities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Activity Type</InputLabel>
                <Select
                  value={activityType}
                  onChange={(e) => setActivityType(e.target.value)}
                  label="Activity Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="LOGIN">Login</MenuItem>
                  <MenuItem value="LOGOUT">Logout</MenuItem>
                  <MenuItem value="PROFILE_UPDATE">Profile Update</MenuItem>
                  <MenuItem value="PASSWORD_CHANGE">Password Change</MenuItem>
                  <MenuItem value="SUBSCRIPTION_PURCHASE">Subscription Purchase</MenuItem>
                  <MenuItem value="SUBSCRIPTION_CANCEL">Subscription Cancel</MenuItem>
                  <MenuItem value="EVENT_REGISTER">Event Registration</MenuItem>
                  <MenuItem value="EVENT_ATTEND">Event Attendance</MenuItem>
                  <MenuItem value="BLOG_VIEW">Blog View</MenuItem>
                  <MenuItem value="BLOG_COMMENT">Blog Comment</MenuItem>
                  <MenuItem value="PAYMENT_SUCCESS">Payment Success</MenuItem>
                  <MenuItem value="PAYMENT_FAILED">Payment Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  label="Date Range"
                >
                  <MenuItem value="1">Last 24 hours</MenuItem>
                  <MenuItem value="7">Last 7 days</MenuItem>
                  <MenuItem value="30">Last 30 days</MenuItem>
                  <MenuItem value="90">Last 90 days</MenuItem>
                  <MenuItem value="365">Last year</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>View</InputLabel>
                <Select
                  value={view}
                  onChange={(e) => setView(e.target.value)}
                  label="View"
                >
                  <MenuItem value="table">Table View</MenuItem>
                  <MenuItem value="timeline">Timeline View</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Tooltip title="Export to CSV">
                <IconButton onClick={handleExport} color="primary">
                  <Download />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Activity Summary */}
      {activitySummary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Activities
                </Typography>
                <Typography variant="h4">
                  {activitySummary.activityByType.reduce((sum, item) => sum + item.count, 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Most Common Activity
                </Typography>
                <Typography variant="h6">
                  {activitySummary.activityByType[0]?._id || 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Daily Average
                </Typography>
                <Typography variant="h4">
                  {Math.round(activitySummary.dailyActivity.reduce((sum, item) => sum + item.count, 0) / activitySummary.dailyActivity.length) || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Period
                </Typography>
                <Typography variant="h6">
                  {activitySummary.period}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Table View */}
      {view === 'table' && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Date & Time</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activities
                .filter(activity => 
                  !searchTerm || 
                  activity.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  activity.type.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((activity) => (
                  <TableRow key={activity._id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {getActivityIcon(activity.type)}
                        <Box ml={1}>
                          <Chip
                            label={activity.type.replace(/_/g, ' ')}
                            color={getActivityColor(activity.type)}
                            size="small"
                          />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{activity.description}</TableCell>
                    <TableCell>{formatActivityDate(activity.createdAt)}</TableCell>
                    <TableCell>{activity.ipAddress || 'N/A'}</TableCell>
                    <TableCell>
                      {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                        <Tooltip title={JSON.stringify(activity.metadata, null, 2)}>
                          <Chip label="View Details" size="small" clickable />
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </TableContainer>
      )}

      {/* Timeline View */}
      {view === 'timeline' && (
        <Paper>
          <List>
            {activities
              .filter(activity => 
                !searchTerm || 
                activity.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                activity.type.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((activity) => (
                <ListItem key={activity._id} divider>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: getActivityColor(activity.type) }}>
                      {getActivityIcon(activity.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={activity.type.replace(/_/g, ' ')}
                    secondary={
                      <Box>
                        <Typography variant="body2">
                          {activity.description}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatActivityDate(activity.createdAt)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default UserActivityHistory;